/**
 * External store for GET /auth/me — the single backend endpoint the frontend
 * consults for permission gating (Task B5).
 *
 * Fetched exactly once per session (deduped via module state + in-flight
 * promise), re-fetched when the session identity changes (different account /
 * token). On failure the snapshot enters 'error' state and useAuth() falls
 * back to legacy Entra group parsing so existing users are never locked out.
 */
import { apiConfig } from '@/config/authConfig';
import type { UserPermissions } from '@ip/shared-types';

export interface AuthMeUser {
  id?: string;
  email?: string;
  displayName?: string | null;
  provider: 'entra' | 'local';
}

export interface AuthMeResponse {
  user: AuthMeUser;
  permissions: UserPermissions;
}

export interface AuthMeSnapshot {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  sessionKey: string | null;
  data: AuthMeResponse | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();

let snapshot: AuthMeSnapshot = { status: 'idle', sessionKey: null, data: null };
let inflight: Promise<void> | null = null;

/** Backoff between /auth/me retries after a failure (see ensureAuthMe). */
const ERROR_RETRY_MS = 10_000;
let lastErrorAt = 0;

function setSnapshot(next: AuthMeSnapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function subscribeAuthMe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAuthMeSnapshot(): AuthMeSnapshot {
  return snapshot;
}

/** Clear cached /auth/me state (logout / session switch). */
export function resetAuthMe(): void {
  inflight = null;
  if (snapshot.status !== 'idle') {
    setSnapshot({ status: 'idle', sessionKey: null, data: null });
  }
}

/**
 * Seed the store from a login response that already contains user +
 * permissions (local login returns them), avoiding an extra round-trip.
 */
export function seedAuthMe(sessionKey: string, data: AuthMeResponse): void {
  inflight = null;
  setSnapshot({ status: 'loaded', sessionKey, data });
}

/**
 * Ensure /auth/me has been fetched for the given session. Safe to call from
 * every useAuth() consumer — only the first call per session triggers a fetch.
 */
export function ensureAuthMe(
  sessionKey: string,
  getToken: () => Promise<string | null>,
): void {
  // 'error' is retryable with a backoff — a single transient failure (deploy
  // blip, one 5xx) must not disable module-aware routing for the whole tab
  // session (null permissions make every client gate fail open for DB-role
  // users). isAuthReady stays true during the window, so nothing blocks.
  if (snapshot.sessionKey === sessionKey && snapshot.status !== 'idle') {
    if (snapshot.status !== 'error') return;
    if (Date.now() - lastErrorAt < ERROR_RETRY_MS) return;
  }
  if (inflight) return;

  setSnapshot({ status: 'loading', sessionKey, data: null });

  inflight = (async () => {
    try {
      const token = await getToken();
      if (!token) throw new Error('No token for /auth/me');
      const response = await fetch(`${apiConfig.baseUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`auth/me HTTP ${response.status}`);
      const data = (await response.json()) as AuthMeResponse;
      // Session may have changed while fetching — only apply if still current.
      if (snapshot.sessionKey === sessionKey) {
        setSnapshot({ status: 'loaded', sessionKey, data });
      }
    } catch (error) {
      console.warn('[Auth] /auth/me failed — falling back to legacy group parsing:', error);
      if (snapshot.sessionKey === sessionKey) {
        lastErrorAt = Date.now();
        setSnapshot({ status: 'error', sessionKey, data: null });
      }
    } finally {
      inflight = null;
    }
  })();
}
